/**
 * Numeracion de la parametrica de orden (GEST0025) a partir del listado del proceso
 * (ordentipos/listado). Documentos padre y otros documentos van de 100 en 100; los hijos
 * toman el orden de su padre mas 1, 2, 3... (factura 100 -> lista de empaque 101).
 * El padre de un hijo es el primero bajo el que lo muestra el listado (relacion padre-hijo
 * de Tipo, en el orden de la consulta por DO).
 */
export const PASO = 100;

//Empresa / proceso cuyo orden actual ya sigue la lista de chequeo de CIMA: al crear se
//precarga con ese orden convertido a la numeracion (Agencia de Aduanas SIACO / Importaciones)
const CON_ORDEN_POR_DEFECTO = ["800251957|100"];
export const tieneOrdenPorDefecto = (empresa, proceso) => CON_ORDEN_POR_DEFECTO.includes(`${empresa}|${proceso}`);

/** El proceso ya tiene la numeracion (algun padre con orden en centenas): no se propone otra */
export const yaNumerado = (listado) =>
  listado.some((f) => f.SECCIONX === "PADRE" && f.ORDENXXX !== null && f.ORDENXXX % 100 === 0);

export const llave = (r) => `${r.EMPNITXX}|${r.AREAIDXX}|${r.SERIEIDX}|${r.TIPOIDXX}`;
const llaveSerie = (r) => `${r.AREAIDXX}|${r.SERIEIDX}`;

/** Sin repetidos, conservando la primera aparicion (un hijo sale una vez por cada padre) */
const unicos = (filas, fn = llave) => {
  const vistos = new Set();
  return filas.filter((f) => !vistos.has(fn(f)) && vistos.add(fn(f)));
};

/** Tipos del listado que van en una seccion (PADRE, HIJO u OTRO), en el orden del listado */
export const candidatos = (listado, seccion) => unicos(listado.filter((f) => f.SECCIONX === seccion));

export const seriesDe = (cands) =>
  unicos(cands, llaveSerie).map((f) => ({ value: llaveSerie(f), label: f.SERIEDES }));

export const tiposDe = (cands, serie) =>
  cands.filter((f) => llaveSerie(f) === serie).map((f) => ({ value: llave(f), label: f.TIPODESX }));

/** Serie ("AREA|SERIE") de una terna "EMPNIT|AREA|SERIE|TIPO" */
export const serieDeTerna = (terna) => terna.split("|").slice(1, 3).join("|");

/** Padre bajo el que se numera un hijo: el primero con el que aparece en el listado */
export const padreDe = (terna, listado) =>
  listado.find((f) => f.SECCIONX === "HIJO" && llave(f) === terna)?.PADREIDX || "";

/** Documento de transporte (AWB, BL, carta porte): AREA 100 / SERIE 100, igual que en gestorapi */
export const esTransporte = (f) => Number(f.AREAIDXX) === 100 && Number(f.SERIEIDX) === 100;

export const nombreDe =(terna, listado) => listado.find((f) => llave(f) === terna)?.TIPODESX || "";

const numero = (valor) => (valor === "" || valor === null || valor === undefined ? null : Number(valor));

/** Orden vigente del padre: el digitado en el modal o, si no, el del listado */
export const ordenPadre = (padre, listado, filasModal) => {
  const enModal = filasModal.PADRE.find((f) => f.terna === padre);
  if (enModal) return numero(enModal.orden);
  return listado.find((f) => llave(f) === padre)?.ORDENXXX ?? null;
};

/** Rango valido del orden: centenas para padres/otros, padre+1..padre+99 para hijos */
export const rangoOrden = (seccion, fila, listado, filasModal) => {
  if (seccion !== "HIJO") return { min: PASO, max: 99900, step: PASO };
  const base = fila.padre ? ordenPadre(fila.padre, listado, filasModal) : null;
  return base === null ? null : { min: base + 1, max: base + PASO - 1, step: 1, base };
};

const cumple = (orden, rango) =>
  orden !== null && rango !== null && orden >= rango.min && orden <= rango.max && orden % rango.step === 0;

/**
 * Orden que se propone al elegir un tipo en una fila nueva: el que ya tiene si cumple la
 * numeracion; si no, la siguiente centena (padres/otros) o el siguiente consecutivo del padre.
 * @returns {number|string} "" cuando el hijo no tiene padre con orden
 */
export const proponerOrden = (seccion, fila, listado, filasModal) => {
  const rango = rangoOrden(seccion, fila, listado, filasModal);
  if (!rango) return "";
  const actual = listado.find((f) => llave(f) === fila.terna)?.ORDENXXX ?? null;
  if (cumple(actual, rango)) return actual;
  const ordenes = filasModal[seccion]
    .filter((f) => f.id !== fila.id && (seccion !== "HIJO" || f.padre === fila.padre))
    .map((f) => numero(f.orden))
    .filter((o) => o !== null);
  if (seccion !== "HIJO") return Math.floor(Math.max(0, ...ordenes) / PASO) * PASO + PASO;
  const hermanos = ordenes.filter((o) => o >= rango.min && o <= rango.max);
  return hermanos.length > 0 ? Math.min(Math.max(...hermanos) + 1, rango.max) : rango.min;
};

/**
 * Numera en el orden del listado: avanza un paso cada vez que cambia el orden actual (los tipos
 * con el mismo orden comparten numero, p. ej. AWB, BL y carta porte) o cuando no tienen orden.
 * @param {Array}    filas  filas del listado en orden
 * @param {function} paso   (n) => orden del n-esimo grupo (1, 2, 3...)
 * @returns {Array} ordenes en el mismo orden de filas
 */
const numerar = (filas, paso) => {
  let n = 0;
  let previo;
  return filas.map((f) => {
    if (f.ORDENXXX === null || f.ORDENXXX !== previo) n += 1;
    previo = f.ORDENXXX;
    return paso(n);
  });
};

let consecutivo = 0;
export const filaVacia = (padre = "") => ({ id: ++consecutivo, padre, serie: "", terna: "", orden: "" });
const filaDe = (f, padre, orden) => ({ id: ++consecutivo, padre, serie: llaveSerie(f), terna: llave(f), orden });

/** Orden que ya tiene el tipo si cumple la numeracion; si no, vacio */
const ordenVigente = (f, rango) => (cumple(f.ORDENXXX, rango) ? f.ORDENXXX : "");

/**
 * Filas del modal con todos los tipos del proceso, cada uno en su seccion; los hijos agrupados
 * bajo su padre. Con ordenPorDefecto se numeran padres e hijos segun el orden actual (lista de
 * chequeo); si no, cada fila trae el orden que ya tenga cuando cumple la numeracion. Otros
 * documentos siempre traen solo el orden que ya tengan.
 * @param {Array}   listado
 * @param {boolean} ordenPorDefecto
 * @returns {{PADRE: Array, HIJO: Array, OTRO: Array}}
 */
export const precargar = (listado, ordenPorDefecto) => {
  const padres = candidatos(listado, "PADRE");
  const otros = candidatos(listado, "OTRO");
  const centenas = { min: PASO, max: 99900, step: PASO };
  const ordenesPadre = ordenPorDefecto ? numerar(padres, (n) => n * PASO) : padres.map((p) => ordenVigente(p, centenas));
  //Otros documentos no se numeran por defecto: solo llevan orden los que deben ir primero y los
  //demas salen por nombre despues, asi cambiar uno no obliga a renumerar la seccion
  const ordenesOtro = otros.map((o) => ordenVigente(o, centenas));
  const hijos = candidatos(listado, "HIJO");
  const filasHijo = padres.flatMap((p, i) => {
    const propios = hijos.filter((h) => h.PADREIDX === llave(p));
    const base = numero(ordenesPadre[i]);
    if (base === null) return propios.map((h) => filaDe(h, llave(p), ""));
    const rango = { min: base + 1, max: base + PASO - 1, step: 1 };
    const ordenes = ordenPorDefecto ? numerar(propios, (n) => Math.min(base + n, rango.max)) : propios.map((h) => ordenVigente(h, rango));
    return propios.map((h, j) => filaDe(h, llave(p), ordenes[j]));
  });
  return {
    PADRE: padres.map((p, i) => filaDe(p, "", ordenesPadre[i])),
    HIJO: filasHijo,
    OTRO: otros.map((o, i) => filaDe(o, "", ordenesOtro[i])),
  };
};
